import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Search } from 'lucide-react'
import type { NestPickerOption, NestPickerProps } from './NestPicker.types'
import './NestPicker.scss'

/**
 * **„Einhängen in …“** (#204, Grill 9): die Aufgabe, in die eingehängt oder umgehängt wird —
 * aus den offenen Aufgaben desselben Projekts, mit Suche, nach Abschnitt gruppiert, in
 * Listenreihenfolge. Ausgegraut, was kein Ziel sein kann.
 *
 * Nur der Inhalt: Das Unter-Menü des **Aufgaben-Menüs** und das Popover in den
 * Aufgaben-Details bringen die Hülle (CLAUDE.md, Overlays).
 *
 * @example
 * <NestPicker groups={groups} onSelect={id => nest(ids, id)} onClose={close} />
 */
export const NestPicker: React.FC<NestPickerProps> = ({ groups, onSelect, onClose }) => {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)

  const shown = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase()
    return groups
      .map(group => ({ ...group, options: group.options.filter(o => !needle || o.title.toLocaleLowerCase().includes(needle)) }))
      .filter(group => group.options.length > 0)
  }, [groups, query])
  const choosable = useMemo(() => shown.flatMap(g => g.options).filter(o => !o.disabled), [shown])
  const current: NestPickerOption | undefined = choosable[Math.min(active, choosable.length - 1)]

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return
    if (e.key === 'Escape') {
      onClose?.()
      return
    }
    // Das Menü darum hat einen Typeahead — getippt wird hier.
    e.stopPropagation()
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      const step = e.key === 'ArrowDown' ? 1 : -1
      setActive(index => Math.max(0, Math.min(choosable.length - 1, index + step)))
    } else if (e.key === 'Enter' && current) {
      e.preventDefault()
      onSelect(current.id)
    }
  }

  return (
    <div className="nest-picker">
      <div className="nest-picker__search">
        <Search size={16} />
        <input
          className="nest-picker__input"
          placeholder={t('taskList.nest.search')}
          aria-label={t('taskList.nest.search')}
          value={query}
          autoFocus
          onChange={e => { setQuery(e.target.value); setActive(0) }}
          onKeyDown={handleKeyDown}
        />
      </div>
      <div className="nest-picker__list" role="listbox">
        {shown.length === 0 && <div className="nest-picker__empty">{t('taskList.nest.noMatch')}</div>}
        {shown.map(group => (
          <div key={group.id} className="nest-picker__group" role="group" aria-label={group.title ?? undefined}>
            {group.title && <div className="context-menu-content__label nest-picker__group-title">{group.title}</div>}
            {group.options.map(option => (
              <button
                key={option.id}
                type="button"
                role="option"
                aria-selected={option.id === current?.id}
                className="context-menu-content__item nest-picker__option"
                data-highlighted={option.id === current?.id ? '' : undefined}
                disabled={option.disabled}
                onPointerMove={() => { if (!option.disabled) setActive(choosable.indexOf(option)) }}
                onClick={() => onSelect(option.id)}
              >
                <span className="nest-picker__title">{option.title}</span>
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}
